import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
} from '@mui/material';
import {
  AccountBalance as BalanceIcon,
  CreditCard as CardIcon,
  Autorenew as AutoRenewIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import PaymentMethodsManager from '../components/payments/PaymentMethodsManager';
import AutoRefillSettings from '../components/payments/AutoRefillSettings';
import DepositDialog from '../components/payments/DepositDialog';
import WithdrawalDialog from '../components/payments/WithdrawalDialog';
import { isStoreDistribution } from '../utils/distribution';

export default function PaymentsSettingsPage() {
  const storeMode = isStoreDistribution();
  const [depositOpen, setDepositOpen] = useState(false);
  const [withdrawalOpen, setWithdrawalOpen] = useState(false);
  const [balance, setBalance] = useState(0); // TODO: Load from API

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Payments & Auto-Refill
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Manage your account balance, payment methods, and auto-refill settings
      </Typography>

      {storeMode && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Some account management actions are only available on the Qixit website.
        </Typography>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Balance Overview */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #56903a 0%, #64903a 100%)', color: 'white' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Available Balance
              </Typography>
              <Typography variant="h3" sx={{ mt: 1 }}>
                ${balance.toFixed(2)}
              </Typography>
            </Box>
            <BalanceIcon sx={{ fontSize: 60, opacity: 0.3 }} />
          </Box>
          
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            {!storeMode && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDepositOpen(true)}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
                }}
              >
                Add Funds
              </Button>
            )}
            {!storeMode && (
            <Button
              variant="contained"
              startIcon={<RemoveIcon />}
              onClick={() => setWithdrawalOpen(true)}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
              }}
            >
              Withdraw
            </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Auto-Refill Settings */}
        {!storeMode && (
          <Grid item xs={12}>
            <AutoRefillSettings />
          </Grid>
        )}

        {/* Payment Methods */}
        {!storeMode && (
          <Grid item xs={12}>
            <PaymentMethodsManager />
          </Grid>
        )}

        {/* Information Cards */}
        {!storeMode && (
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CardIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Payment Options</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                <strong>Credit/Debit Cards:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • Fee: 2.9% + $0.30 per transaction
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • Instant processing
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • Best for: One-time deposits
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        )}

        {!storeMode && (
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BalanceIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">ACH Bank Account</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                <strong>Save up to 70% on fees!</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • Fee: 0.8% (capped at $5)
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • Processing: 3-5 business days
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • Best for: Auto-refill, large amounts
              </Typography>
              <Typography variant="body2" color="success.main" fontWeight="bold" sx={{ mt: 2 }}>
                💰 Example: $500 = $4 (ACH) vs $14.80 (card) = Save $10.80!
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        )}

        {!storeMode && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AutoRenewIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">About Auto-Refill</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                <strong>Never run out of credits again!</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Auto-refill automatically tops off your account when your balance falls below your chosen threshold. 
                Set it once and forget it - your account will always have funds when you need them.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                <strong>How it works:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                <ul style={{ marginTop: 0, paddingLeft: 20 }}>
                  <li>Choose your threshold (e.g., $50)</li>
                  <li>Choose your refill amount (e.g., $200)</li>
                  <li>Select a saved payment method</li>
                  <li>We check your balance every hour</li>
                  <li>When balance drops below threshold, we automatically charge your payment method</li>
                  <li>Credits are added to your account within minutes (card) or 3-5 days (ACH)</li>
                </ul>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Safety features:</strong> Auto-disables after 3 failed attempts. You can disable anytime.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        )}
      </Grid>

      {/* Dialogs */}
      {!storeMode && (
        <DepositDialog
          open={depositOpen}
          onClose={() => setDepositOpen(false)}
          onSuccess={() => {
            // TODO: Reload balance
            setDepositOpen(false);
          }}
        />
      )}

      {!storeMode && (
        <WithdrawalDialog
          open={withdrawalOpen}
          onClose={() => setWithdrawalOpen(false)}
          onSuccess={() => {
            setWithdrawalOpen(false);
          }}
        />
      )}
    </Container>
  );
}
